import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Table, InputGroup, FormControl, Button, Spinner } from 'react-bootstrap';
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
  useResizeColumns,
  useFlexLayout,
  useFilters,
} from 'react-table';

const Container = tw.div`container bg-white rounded border border-gray-200 shadow-lg p-4`;

const Header = tw.div`flex justify-between items-center mb-4`;
const Title = tw.h2`text-gray-800 font-semibold text-xl`;
const TableWrapper = tw.div`mt-4 overflow-x-auto`;

const StyledTable = styled(Table)`
  ${tw`min-w-full`}
  font-size: 11px;
  font-family: 'Arial', sans-serif;
  border: 1px solid #ccc;
  border-collapse: collapse;

  thead {
    ${tw`bg-gray-200`}

    th {
      ${tw`border border-gray-300 text-center`}
      background-color: #f0f0f0;
      font-weight: bold;
      white-space: normal;
      text-overflow: ellipsis;
      overflow: hidden;
      padding: 8px;
      position: relative;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }
  }

  tbody {
    tr {
      ${tw`border-b border-gray-200`}

      &:hover {
        ${tw`bg-gray-100`}
      }
    }

    td {
      ${tw`border border-gray-300 py-2 px-4`}
      padding: 8px;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
      text-align: right; /* Align numeric cells to the right */
    }

    .align-left {
      text-align: left;
    }
  }
`;

const FilterInput = styled(FormControl)`
  ${tw`border border-gray-300`}
  height: 30px;
  font-size: 14px;
`;

const RefreshButton = styled(Button)`
  ${tw`ml-2`}
`;

const ResizableHeader = ({ className, column }) => {
  const { getHeaderProps, getSortByToggleProps, isSorted, isSortedDesc, canResize } = column;

  return (
    <th className={className} {...getHeaderProps()}>
      <div style={{ position: 'relative', width: '100%' }}>
        <span>{column.render('Header')}</span>
        {column.canSort && (
          <button
            className="sort-button"
            {...getSortByToggleProps()}
            style={{ padding: '0', marginLeft: '5px', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {isSorted ? (isSortedDesc ? '↓' : '↑') : '↕'}
          </button>
        )}
        {canResize && (
          <div
            {...column.getResizerProps()}
            className="resizer"
            style={{ right: 0, position: 'absolute', top: 0, bottom: 0, cursor: 'col-resize', width: '10px' }}
          />
        )}
      </div>
    </th>
  );
};

const GlobalCurrency = () => {
  const [globalInstruments, setGlobalInstruments] = useState([]);
  const [loading, setLoading] = useState(false);

  const logoUrls = {
    USD: 'https://flagcdn.com/w320/us.png',
    GBP: 'https://flagcdn.com/w320/gb.png',
    EUR: 'https://flagcdn.com/w320/eu.png',
    JPY: 'https://flagcdn.com/w320/jp.png',
    CNY: 'https://flagcdn.com/w320/cn.png',
    INR: 'https://flagcdn.com/w320/in.png',
    'Dollar Index': 'https://flagcdn.com/w320/us.png', // Added Dollar Index logo
  };
  
  

  const LogoCell = ({ cell: { value } }) => {
    // console.log('LogoCell value:', value); // Debug log
    const baseCurrency = value.includes('/') ? value.split('/')[0] : value;
    const imageUrl = logoUrls[baseCurrency];
    // console.log('baseCurrency:', baseCurrency); // Debug log
    // console.log('imageUrl:', imageUrl); // Debug log
    return imageUrl ? <img src={imageUrl} alt={baseCurrency} style={{ maxWidth: '30px', maxHeight: '30px' }} /> : null;
  };

  const NumericCell = ({ cell: { value } }) => <span>{parseFloat(value).toFixed(2)}</span>;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setLoading(true);
    axios
      .get('https://falcon-algo.onrender.com/api/summary/globalcurrency')
      .then((response) => {
        // console.log('API Response:', response.data);
        setGlobalInstruments(response.data.data);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const ChangePercentCell = ({ cell: { value } }) => {
    if (isNaN(value)) {
      return <span>{value}%</span>;
    }
    return <span style={{ color: value >= 0 ? 'green' : 'red' }}>{parseFloat(value).toFixed(2)}%</span>;
  };

  const ChangeValueCell = ({ cell: { value } }) => {
    if (isNaN(value)) {
      return <span>{value}%</span>;
    }
    return <span style={{ color: value >= 0 ? 'green' : 'red' }}>{parseFloat(value).toFixed(2)}%</span>;
  };

  const MarketStateCell = ({ cell: { value } }) => (
    <span style={{ color: value === 'closed' ? 'red' : 'green' }}>{value}</span>
  );

  const TimestampCell = ({ cell: { value } }) => (
    <span>{new Date(value).toLocaleString()}</span>
  );

  const columns = useMemo(
    () => [
      { Header: 'Short Name', accessor: 'name', width: 100 },
      { Header: 'Logo', accessor: 'name', id: 'logo', Cell: LogoCell, width: 100 },
      { Header: 'Last Price', accessor: 'ltp', Cell: NumericCell, align: 'right', width: 90 },
      { Header: 'Change', accessor: 'chg', Cell: ChangeValueCell, align: 'right', width: 80 },
      { Header: 'Percent Change', accessor: 'chgper', Cell: ChangePercentCell, align: 'right', width: 130 },
      { Header: 'High', accessor: 'high', Cell: NumericCell, align: 'right', width: 80 },
      { Header: 'Low', accessor: 'low', Cell: NumericCell, align: 'right', width: 80 },
      { Header: 'Open', accessor: 'open', Cell: NumericCell, align: 'right', width: 80 },
      { Header: 'Updated At', accessor: 'time', Cell: TimestampCell, align: 'right', width: 100 },
      { Header: 'Market State', accessor: 'market_state',Cell: MarketStateCell, width: 100 },
      { Header: 'Previous Close', accessor: 'prevclose', Cell: NumericCell, align: 'right', width: 110 },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: globalInstruments,
      initialState: { pageIndex: 0, pageSize: 10, globalFilter: '' },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useResizeColumns,
    useFlexLayout
  );

  return (
    <Container>
      <Header>
        <Title>Global Currency</Title>
        <RefreshButton onClick={fetchData} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </RefreshButton>
      </Header>
      <InputGroup className="mb-3">
        <FilterInput
          value={state.globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
        />
      </InputGroup>
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
          </Spinner>
            <span className="sr-only">Loading...</span>
        </div>
      ) : (
        <TableWrapper>
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <ResizableHeader
                      key={column.id}
                      column={column}
                      className="text-center"
                    />
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className={cell.column.align === 'right' ? 'align-right' : 'align-left'}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </TableWrapper>
      )}
    </Container>
  );
};

export default GlobalCurrency
